import { useState, useMemo } from 'react'
import ReplayWorkspace from '../../workspaceReplay2/ReplayWorkspace'
import ReplayControls from '../../workspaceReplay2/components/ReplayControls'
import StudentLabelingSidebar from '../../workspaceReplay2/components/StudentLabelingSidebar'
import { usePlayback } from '../../workspaceReplay2/hooks/usePlayback'
import { autoSegmentEvents } from '../../workspaceReplay2/utils/autoSegmentation'
import { computeStudentAnalytics } from './computeSessionStats'

// ─── Helpers ────────────────────────────────────────────────────────────────────

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '0s'
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`
  const h = Math.floor(m / 60)
  const rm = m % 60
  return rm > 0 ? `${h}h ${rm}m` : `${h}h`
}

function formatEventTime(seconds) {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function formatFullTime(iso) {
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

const intentLabels = {
  clarification: { label: 'Clarification', desc: 'Asking for explanation' },
  debugging: { label: 'Debugging', desc: 'Fixing an error' },
  conceptual: { label: 'Conceptual', desc: 'Understanding a concept' },
  'solution-request': { label: 'Solution Request', desc: 'Asking for direct answer' },
  'i-dont-know': { label: "I don't know", desc: 'Expressing confusion' },
}

const segmentColors = {
  reading: '#e0e7ff',
  coding: '#818cf8',
  testing: '#a5b4fc',
  'ai-interaction': '#6366f1',
  idle: '#d1d5db',
}

const segmentLabels = {
  reading: 'Reading',
  coding: 'Coding',
  testing: 'Testing',
  'ai-interaction': 'AI Chat',
  idle: 'Idle',
}


// ─── Behavior Sequence Bar ──────────────────────────────────────────────────────

function BehaviorBar({ segments, totalDuration }) {
  if (!segments || segments.length === 0 || !totalDuration) {
    return (
      <div style={{ height: '24px', borderRadius: '6px', backgroundColor: '#f0f0f2' }} />
    )
  }
  const seen = new Map()
  segments.forEach(s => {
    const id = s.behavior?.id || 'unknown'
    if (!seen.has(id)) seen.set(id, { label: s.behavior?.label || id, color: s.behavior?.color || '#9ca3af' })
  })

  return (
    <div>
      <div style={{
        display: 'flex', height: '24px', borderRadius: '6px', overflow: 'hidden',
        border: '1px solid #e8e8eb',
      }}>
        {segments.map((seg, i) => {
          const dur = seg.endTime - seg.startTime
          const pct = (dur / totalDuration) * 100
          if (pct < 0.3) return null
          const color = seg.behavior?.color || '#9ca3af'
          return (
            <div
              key={i}
              title={`${seg.behavior?.label || 'Unknown'} (${Math.round(dur / 1000)}s)`}
              style={{
                width: `${pct}%`,
                backgroundColor: color,
                minWidth: '2px',
                borderRight: i < segments.length - 1 ? '1px solid rgba(255,255,255,0.3)' : 'none',
              }}
            />
          )
        })}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '8px' }}>
        {[...seen.entries()].map(([id, { label, color }]) => (
          <div key={id} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: color }} />
            <span style={{ fontSize: '10px', color: '#888' }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Behavior Distribution ──────────────────────────────────────────────────────

function BehaviorDistribution({ segments, totalDuration }) {
  if (!segments || segments.length === 0 || !totalDuration) {
    return (
      <div style={{ padding: '12px', color: '#888', fontSize: '12px', textAlign: 'center', border: '1px dashed #e8e8eb', borderRadius: '6px' }}>
        No behavior data
      </div>
    )
  }

  const byBehavior = new Map()
  segments.forEach(s => {
    const id = s.behavior?.id || 'unknown'
    const dur = s.endTime - s.startTime
    if (!byBehavior.has(id)) {
      byBehavior.set(id, { label: s.behavior?.label || id, color: s.behavior?.color || '#9ca3af', time: 0 })
    }
    byBehavior.get(id).time += dur
  })

  const sorted = [...byBehavior.values()].sort((a, b) => b.time - a.time)
  const totalMs = sorted.reduce((sum, b) => sum + b.time, 0) || 1

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {sorted.map(b => {
        const pct = (b.time / totalMs) * 100
        const seconds = Math.round(b.time / 1000)
        return (
          <div key={b.label}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: b.color }} />
                <span style={{ fontSize: '11px', color: '#555' }}>{b.label}</span>
              </div>
              <span style={{ fontSize: '11px', color: '#888' }}>{formatDuration(seconds)} ({Math.round(pct)}%)</span>
            </div>
            <div style={{ height: '6px', backgroundColor: '#f0f0f2', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, backgroundColor: b.color, borderRadius: '3px' }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function StatCard({ label, value, sub }) {
  return (
    <div style={{
      padding: '12px',
      borderRadius: '8px',
      border: '1px solid #e8e8eb',
      backgroundColor: '#fafafa',
    }}>
      <div style={{ fontSize: '11px', color: '#888' }}>{label}</div>
      <div style={{ fontSize: '14px', fontWeight: 600, color: '#111', marginTop: '2px' }}>{value}</div>
      {sub && <div style={{ fontSize: '10px', color: '#888', marginTop: '2px' }}>{sub}</div>}
    </div>
  )
}

// ─── Metric Row ─────────────────────────────────────────────────────────────────

function MetricRow({ label, value, desc }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px',
      borderRadius: '8px',
      border: '1px solid #e8e8eb',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12px', fontWeight: 500, color: '#111' }}>{label}</div>
        <div style={{ fontSize: '11px', color: '#888' }}>{desc}</div>
      </div>
      <span style={{ fontSize: '14px', fontWeight: 600, color: '#111', flexShrink: 0 }}>{value}</span>
    </div>
  )
}

// ─── Tabs ───────────────────────────────────────────────────────────────────────

function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{
      display: 'flex', gap: '0', borderBottom: '1px solid #e8e8eb',
      padding: '0 24px',
    }}>
      {tabs.map(tab => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          style={{
            padding: '10px 16px',
            fontSize: '12px',
            fontWeight: active === tab.key ? 600 : 400,
            color: active === tab.key ? '#0070CC' : '#888',
            background: 'none',
            border: 'none',
            borderBottom: active === tab.key ? '2px solid #0070CC' : '2px solid transparent',
            cursor: 'pointer',
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

// ─── Main Component ─────────────────────────────────────────────────────────────

export function StudentDetail({ student, rawEvents = [], precomputedAnalytics = null, sessionData, sessionId, isGuiSession = false, studentUserId, onBack, onTabChange, initialTab = 'overview', caseStudyData = null }) {
  const [activeTab, setActiveTab] = useState(initialTab)
  const handleTabChange = (tab) => {
    setActiveTab(tab)
    if (onTabChange) onTabChange(tab)
  }
  const [behaviorScope, setBehaviorScope] = useState('full')
  const [replaySidebarOpen, setReplaySidebarOpen] = useState(true)
  const [aiUsageView, setAiUsageView] = useState('stats')
  const [regionView, setRegionView] = useState('stats')
  const [overviewBarView, setOverviewBarView] = useState('sequence')

  // ── Single source of truth: use pre-computed if available, else compute ──
  const analytics = useMemo(() => {
    if (precomputedAnalytics) return precomputedAnalytics
    return computeStudentAnalytics({
      rawEvents,
      conversation: student.conversation,
      autoSegmentEvents,
    })
  }, [precomputedAnalytics, rawEvents, student.conversation])

  const {
    filteredEvents, replayStartTime, replayDuration,
    sessionStats, firstQueryEvent, firstQueryTime, firstQueryMs,
    timeToFirstRun, timeToCompletion, runsBeforeAllPass, codeAtFirstQuery,
    autoSegments, visibleSegments, beforeSegments, afterSegments, timelineSegments,
    behavior, beforeStats, afterStats, aiProgressionTimeline, testScoreProgression,
    conversation, userMessages, intentCounts, messageCount,
  } = analytics

  // ── Case study: real before-query data from loaded deployments ──
  const caseA = caseStudyData?.studentA || null
  const caseB = caseStudyData?.studentB || null

  const playback = usePlayback(replayDuration)

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000)
    const m = Math.floor(totalSeconds / 60)
    const s = totalSeconds % 60
    return `${m}:${s < 10 ? '0' : ''}${s}`
  }

  const msToSec = (ms) => ms != null ? Math.round(ms / 1000) : 0

  const scopedStats = behaviorScope === 'before-query' ? sessionStats.before
    : behaviorScope === 'after-query' ? sessionStats.after
    : sessionStats.full
  const scopedDuration = Math.round((scopedStats.periodDuration || 0) / 1000)
  const derivedBehavior = behavior[behaviorScope]

  const filteredReplayEvents = filteredEvents

  const firstError = filteredEvents.find(e => e.type === 'TERMINAL_ERROR')
  const firstErrorTime = firstError ? Math.round((firstError.timestamp - replayStartTime) / 1000) : null

  const sectionStyle = { marginBottom: '24px' }
  const sectionTitle = { fontSize: '12px', fontWeight: 600, color: '#111', marginBottom: '12px' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #e8e8eb', flexShrink: 0 }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            fontSize: '12px', color: '#888', background: 'none',
            border: 'none', cursor: 'pointer', padding: '4px 8px',
            marginLeft: '-8px', marginBottom: '12px', borderRadius: '6px',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          ← Back to list
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '50%',
            backgroundColor: '#f0f0f2', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#111', fontFamily: 'monospace' }}>
              {student.name}
            </div>
            <div style={{ fontSize: '12px', color: '#888' }}>
              Submitted {formatFullTime(student.submissionTime)}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: '#888' }}>
            <span>Tests: {sessionStats.full.testResults.lastPassedCount ?? '?'}/{sessionStats.full.testResults.lastTotalTests ?? '?'}</span>
            <span>{formatDuration(msToSec(sessionStats.full.periodDuration))}</span>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <TabBar
        tabs={[
          { key: 'overview', label: 'Overview' },
          { key: 'replay', label: 'Replay' },
          { key: 'ai-usage', label: 'AI Usage' },
          { key: 'behavior', label: 'Behavior' },
        ]}
        active={activeTab}
        onChange={handleTabChange}
      />

      {/* Tab Content */}
      <div style={{
        flex: 1,
        overflowY: activeTab === 'replay' ? 'hidden' : 'auto',
        padding: activeTab === 'replay' ? '0' : '24px',
        display: 'flex',
        flexDirection: 'column',
      }}>

        {/* ── Overview ──────────────────────────────────────────────── */}
        {activeTab === 'overview' && (
          <div>
            <div style={{ ...sectionStyle, padding: '16px', borderRadius: '8px', border: '1px solid #e8e8eb', backgroundColor: '#fafafa' }}>
              <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>Session Summary</div>
              <div style={{ fontSize: '13px', color: '#111', lineHeight: 1.6 }}>{student.behavioralSummary}</div>
            </div>

            <div style={{ ...sectionStyle, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
              <StatCard label="Total Duration" value={formatDuration(msToSec(sessionStats.full.periodDuration))} />
              <StatCard label="Errors" value={`${sessionStats.full.frequency.terminalError.count}`} sub={firstErrorTime !== null ? `First at ${formatDuration(firstErrorTime)}` : 'None'} />
              <StatCard label="Test Runs" value={`${sessionStats.full.frequency.terminalRun.count}`} sub={timeToFirstRun !== null ? `First at ${formatDuration(timeToFirstRun)}` : 'None'} />
              <StatCard label="AI Queries" value={`${sessionStats.full.frequency.chatQuery.count}`} sub={firstQueryTime !== null ? `First at ${formatDuration(Math.round(firstQueryTime))}` : 'None used'} />
            </div>

            {firstQueryTime !== null && (
              <div style={sectionStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={sectionTitle}>Before vs. After First AI Query</div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {['sequence', 'distribution'].map(v => (
                      <button key={v} onClick={() => setOverviewBarView(v)} style={{ padding: '3px 10px', fontSize: '11px', fontWeight: 500, borderRadius: '4px', border: 'none', cursor: 'pointer', backgroundColor: overviewBarView === v ? '#0070CC' : '#f5f5f5', color: overviewBarView === v ? 'white' : '#555' }}>{v === 'sequence' ? 'Sequence' : 'Distribution'}</button>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e8e8eb' }}>
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '10px' }}>Before Query ({formatDuration(Math.round(firstQueryTime))})</div>
                    <div style={{ marginBottom: '12px' }}>
                      {overviewBarView === 'sequence' ? <BehaviorBar segments={beforeSegments} totalDuration={firstQueryMs} /> : <BehaviorDistribution segments={beforeSegments} totalDuration={firstQueryMs} />}
                    </div>
                    {[['Code Edits', beforeStats.frequency.codeEdit.count], ['Runs', beforeStats.frequency.terminalRun.count], ['Errors', beforeStats.frequency.terminalError.count], ['Net Code Growth', `${beforeStats.codeMetrics.netCodeGrowth > 0 ? '+' : ''}${beforeStats.codeMetrics.netCodeGrowth}`], ['Code at First Query', codeAtFirstQuery !== null ? `${codeAtFirstQuery} chars` : 'N/A']].map(([label, val]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}><span style={{ fontSize: '12px', color: '#888' }}>{label}</span><span style={{ fontSize: '12px', fontWeight: 500, color: '#111' }}>{val}</span></div>
                    ))}
                  </div>
                  <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e8e8eb' }}>
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '10px' }}>After Query ({formatDuration(msToSec(afterStats.periodDuration))})</div>
                    <div style={{ marginBottom: '12px' }}>
                      {overviewBarView === 'sequence' ? <BehaviorBar segments={afterSegments} totalDuration={afterStats.periodDuration} /> : <BehaviorDistribution segments={afterSegments} totalDuration={afterStats.periodDuration} />}
                    </div>
                    {[['Code Edits', afterStats.frequency.codeEdit.count], ['Runs', afterStats.frequency.terminalRun.count], ['Errors', afterStats.frequency.terminalError.count], ['Net Code Growth', `${afterStats.codeMetrics.netCodeGrowth > 0 ? '+' : ''}${afterStats.codeMetrics.netCodeGrowth}`]].map(([label, val]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}><span style={{ fontSize: '12px', color: '#888' }}>{label}</span><span style={{ fontSize: '12px', fontWeight: 500, color: '#111' }}>{val}</span></div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div style={sectionStyle}>
              <div style={sectionTitle}>Test Case Results</div>
              {sessionStats.full.testResults.lastTotalTests !== null ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', borderRadius: '8px', border: '1px solid #e8e8eb', marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 500, color: '#111' }}>Final Result</span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: sessionStats.full.testResults.lastPassedCount === sessionStats.full.testResults.lastTotalTests ? '#10b981' : '#f59e0b' }}>{sessionStats.full.testResults.lastPassedCount}/{sessionStats.full.testResults.lastTotalTests} passed</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                    <div style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid #e8e8eb' }}><div style={{ fontSize: '11px', color: '#888' }}>Runs with Passes</div><div style={{ fontSize: '16px', fontWeight: 600, color: '#10b981' }}>{sessionStats.full.testResults.passCount}</div></div>
                    <div style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid #e8e8eb' }}><div style={{ fontSize: '11px', color: '#888' }}>Runs with Failures</div><div style={{ fontSize: '16px', fontWeight: 600, color: '#ef4444' }}>{sessionStats.full.testResults.failCount}</div></div>
                    <div style={{ padding: '10px 12px', borderRadius: '6px', border: '1px solid #e8e8eb' }}><div style={{ fontSize: '11px', color: '#888' }}>Runs Before All-Pass</div><div style={{ fontSize: '16px', fontWeight: 600, color: '#111' }}>{runsBeforeAllPass !== null ? runsBeforeAllPass : 'Never completed'}</div></div>
                  </div>
                </>
              ) : (
                <div style={{ padding: '16px', textAlign: 'center', color: '#888', fontSize: '12px', border: '1px dashed #e8e8eb', borderRadius: '8px' }}>No test results recorded</div>
              )}
            </div>

            {testScoreProgression.length > 1 && (
              <div style={sectionStyle}>
                <div style={sectionTitle}>Test Score Progression</div>
                <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e8e8eb' }}>
                  {(() => {
                    const maxTotal = Math.max(...testScoreProgression.map(t => t.total), 1)
                    const maxTime = testScoreProgression[testScoreProgression.length - 1].relativeTime || 1
                    const w = 600, h = 120, pad = { top: 10, right: 12, bottom: 24, left: 28 }
                    const chartW = w - pad.left - pad.right, chartH = h - pad.top - pad.bottom
                    const x = (t) => pad.left + (t / maxTime) * chartW
                    const y = (v) => pad.top + chartH - (v / maxTotal) * chartH
                    let pathD = ''
                    testScoreProgression.forEach((pt, i) => { const px = x(pt.relativeTime); const py = y(pt.passed); if (i === 0) pathD += `M ${px} ${py}`; else pathD += ` H ${px} V ${py}` })
                    pathD += ` H ${x(maxTime)}`
                    return (
                      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block', width: '100%', maxWidth: `${w}px` }}>
                        {Array.from({ length: maxTotal + 1 }, (_, i) => (<line key={i} x1={pad.left} x2={w - pad.right} y1={y(i)} y2={y(i)} stroke="#f0f0f2" strokeWidth="1" />))}
                        {[0, maxTotal].map(v => (<text key={v} x={pad.left - 6} y={y(v) + 3} textAnchor="end" style={{ fontSize: '9px', fill: '#aaa' }}>{v}</text>))}
                        <path d={pathD} fill="none" stroke="#0070CC" strokeWidth="2" />
                        {testScoreProgression.map((pt, i) => (<circle key={i} cx={x(pt.relativeTime)} cy={y(pt.passed)} r="3" fill={pt.passed === pt.total ? '#10b981' : '#f59e0b'} />))}
                        {[0, Math.round(maxTime / 2), maxTime].map(t => (<text key={t} x={x(t)} y={h - 4} textAnchor="middle" style={{ fontSize: '9px', fill: '#aaa' }}>{formatDuration(t)}</text>))}
                      </svg>
                    )
                  })()}
                </div>
              </div>
            )}

            {/* ── Case Comparison ─────────────────────────────────── */}
            {caseA && caseB && (
            <div style={sectionStyle}>
              <div style={sectionTitle}>Case Study: Two Paths to "I Don't Know"</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e8e8eb' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#111', marginBottom: '2px' }}>Student A</div>
                  <div style={{ fontSize: '11px', color: '#888', marginBottom: '10px' }}>Before query ({formatDuration(Math.round(caseA.duration / 1000))})</div>
                  <div style={{ marginBottom: '12px', position: 'relative' }}>
                    <BehaviorBar segments={caseA.segments} totalDuration={caseA.duration} />
                    {(caseA.runTimes || []).map((t, i) => (
                      <div key={i} title={`Run at ${formatDuration(Math.round(t / 1000))}`} style={{ position: 'absolute', left: `${caseA.duration > 0 ? (t / caseA.duration) * 100 : 0}%`, top: '12px', transform: 'translate(-50%, -50%)', fontSize: '14px', color: '#fff', lineHeight: 1, filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))', zIndex: 15, pointerEvents: 'none' }}>▶</div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', gap: '12px' }}><span style={{ fontSize: '12px', color: '#888', flexShrink: 0 }}>Query Text</span><span style={{ fontSize: '12px', fontWeight: 500, color: '#111', textAlign: 'right', wordBreak: 'break-word', maxWidth: '60%' }}>"{caseA.firstQueryText}"</span></div>
                  {[['Code Edits', caseA.stats.edits], ['Chars Deleted', caseA.stats.charsDeleted], ['Runs', caseA.stats.runs], ['Net Code Growth', `${caseA.stats.netCodeGrowth > 0 ? '+' : ''}${caseA.stats.netCodeGrowth}`]].map(([label, val]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}><span style={{ fontSize: '12px', color: '#888' }}>{label}</span><span style={{ fontSize: '12px', fontWeight: 500, color: '#111' }}>{val}</span></div>
                  ))}
                </div>
                <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e8e8eb' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#111', marginBottom: '2px' }}>Student B</div>
                  <div style={{ fontSize: '11px', color: '#888', marginBottom: '10px' }}>Before query ({formatDuration(Math.round(caseB.duration / 1000))})</div>
                  <div style={{ marginBottom: '12px', position: 'relative' }}>
                    <BehaviorBar segments={caseB.segments} totalDuration={caseB.duration} />
                    {(caseB.runTimes || []).map((t, i) => (
                      <div key={i} title={`Run at ${formatDuration(Math.round(t / 1000))}`} style={{ position: 'absolute', left: `${caseB.duration > 0 ? (t / caseB.duration) * 100 : 0}%`, top: '12px', transform: 'translate(-50%, -50%)', fontSize: '14px', color: '#fff', lineHeight: 1, filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))', zIndex: 15, pointerEvents: 'none' }}>▶</div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', gap: '12px' }}><span style={{ fontSize: '12px', color: '#888', flexShrink: 0 }}>Query Text</span><span style={{ fontSize: '12px', fontWeight: 500, color: '#111', textAlign: 'right', wordBreak: 'break-word', maxWidth: '60%' }}>"{caseB.firstQueryText}"</span></div>
                  {[['Code Edits', caseB.stats.edits], ['Chars Deleted', caseB.stats.charsDeleted], ['Runs', caseB.stats.runs], ['Net Code Growth', `${caseB.stats.netCodeGrowth > 0 ? '+' : ''}${caseB.stats.netCodeGrowth}`]].map(([label, val]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}><span style={{ fontSize: '12px', color: '#888' }}>{label}</span><span style={{ fontSize: '12px', fontWeight: 500, color: '#111' }}>{val}</span></div>
                  ))}
                </div>
              </div>
            </div>
            )}

          </div>
        )}

        {/* ── Replay ────────────────────────────────────────────────── */}
        {activeTab === 'replay' && (
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden', borderRadius: '0 0 12px 12px', position: 'relative' }}>
            {filteredReplayEvents.length === 0 ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#1f2937' }}>
                <div style={{ textAlign: 'center', color: '#9ca3af' }}>
                  <div style={{ fontSize: '14px', fontWeight: 500 }}>No replay data available</div>
                  <div style={{ fontSize: '12px', marginTop: '4px' }}>This student has no telemetry events to replay.</div>
                </div>
              </div>
            ) : (
              <>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#1f2937' }}>
                  <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                    <div style={{ width: '133.33%', height: '133.33%', transform: 'scale(0.75)', transformOrigin: 'top left' }}>
                      <ReplayWorkspace playback={playback} events={filteredReplayEvents} startTime={replayStartTime} task={sessionData?.taskDescription || ''} starterCode={sessionData?.starterCode || ''} testCases={sessionData?.testCases || []} isGuiSession={isGuiSession} />
                    </div>
                  </div>
                  <ReplayControls currentTime={playback.currentTime} duration={playback.duration} isPlaying={playback.isPlaying} playbackSpeed={playback.playbackSpeed} onTogglePlay={playback.togglePlay} onSeek={playback.seek} onReset={playback.reset} onCycleSpeed={playback.cycleSpeed} taggedBlocks={autoSegments.filter(s => !s.isSystemEvent)} showLabeling={true} activeRecording={null} isAutoMode={true} autoSegments={autoSegments} onAssignBehavior={() => {}} formatTime={formatTime} viewOnly={true} events={filteredReplayEvents} startTime={replayStartTime} />
                </div>

                <div style={{ width: replaySidebarOpen ? '280px' : '0px', flexShrink: 0, borderLeft: replaySidebarOpen ? '1px solid #e5e7eb' : 'none', overflow: 'hidden', display: 'flex', flexDirection: 'column', transition: 'width 0.2s ease', backgroundColor: '#ffffff' }}>
                  {replaySidebarOpen && (
                    <StudentLabelingSidebar activeRecording={null} taggedBlocks={autoSegments.filter(s => !s.isSystemEvent)} handleStartRecording={() => {}} handleStopRecording={() => {}} handleDeleteBlock={() => {}} handleBlockClick={(block) => playback.seek(block.startTime)} handleEditBlock={() => {}} formatTime={formatTime} onOpenCodebook={() => {}} showStartButtons={false} showTagsSection={true} emptyMessage="No segments detected." events={filteredReplayEvents} startTime={replayStartTime} currentTime={playback.currentTime} />
                  )}
                </div>

                <button onClick={() => setReplaySidebarOpen(!replaySidebarOpen)} style={{ position: 'absolute', top: '12px', right: replaySidebarOpen ? '284px' : '4px', width: '28px', height: '28px', borderRadius: '6px', border: '1px solid #374151', backgroundColor: '#1f2937', color: '#9ca3af', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', zIndex: 20, transition: 'right 0.2s ease' }} title={replaySidebarOpen ? 'Hide sidebar' : 'Show sidebar'}>{replaySidebarOpen ? '▶' : '◀'}</button>
              </>
            )}
          </div>
        )}

        {/* ── AI Usage ──────────────────────────────────────────────── */}
        {activeTab === 'ai-usage' && (
          <div>
            {messageCount === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 0', textAlign: 'center' }}>
                <div style={{ fontSize: '14px', fontWeight: 500, color: '#111' }}>No AI usage</div>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>This student did not use the LLM assistant</div>
              </div>
            ) : (
              <>
                <div style={{ ...sectionStyle, display: 'flex', gap: '8px' }}>
                  {[{ value: 'stats', label: 'Stats' }, { value: 'code-impact', label: 'Code Impact' }, { value: 'transcript', label: 'Transcript' }].map(opt => (
                    <button key={opt.value} onClick={() => setAiUsageView(opt.value)} style={{ padding: '6px 14px', fontSize: '12px', fontWeight: 500, borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: aiUsageView === opt.value ? '#0070CC' : '#f5f5f5', color: aiUsageView === opt.value ? 'white' : '#555' }}>{opt.label}</button>
                  ))}
                </div>

                {aiUsageView === 'stats' && (
                  <>
                    <div style={{ ...sectionStyle, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                      <StatCard label="Total Queries" value={`${sessionStats.full.frequency.chatQuery.count}`} />
                      <StatCard label="Avg Query Length" value={sessionStats.full.chatMetrics.avgQueryLength !== null ? `${sessionStats.full.chatMetrics.avgQueryLength} chars` : 'N/A'} />
                      <StatCard label="Time to First Query" value={firstQueryTime !== null ? formatDuration(Math.round(firstQueryTime)) : 'N/A'} />
                      <StatCard label="Query Cadence" value={sessionStats.full.chatMetrics.queryCadence.avg !== null ? formatDuration(msToSec(sessionStats.full.chatMetrics.queryCadence.avg)) : 'N/A'} sub={sessionStats.full.chatMetrics.queryCadence.min !== null ? `${formatDuration(msToSec(sessionStats.full.chatMetrics.queryCadence.min))} – ${formatDuration(msToSec(sessionStats.full.chatMetrics.queryCadence.max))}` : null} />
                    </div>

                    <div style={{ ...sectionStyle, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                      <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e8e8eb' }}><div style={{ fontSize: '12px', fontWeight: 500, color: '#111', marginBottom: '4px' }}>Chat → Code Latency</div><div style={{ fontSize: '20px', fontWeight: 600, color: '#111' }}>{sessionStats.full.chatMetrics.chatToCodeLatency !== null ? formatDuration(msToSec(sessionStats.full.chatMetrics.chatToCodeLatency)) : 'N/A'}</div><div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>AI response to next code edit</div></div>
                      <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e8e8eb' }}><div style={{ fontSize: '12px', fontWeight: 500, color: '#111', marginBottom: '4px' }}>Error → Query</div><div style={{ fontSize: '20px', fontWeight: 600, color: '#111' }}>{sessionStats.full.chatMetrics.timeToHelp !== null ? formatDuration(msToSec(sessionStats.full.chatMetrics.timeToHelp)) : 'N/A'}</div><div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>Terminal error to AI query</div></div>
                      <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e8e8eb' }}><div style={{ fontSize: '12px', fontWeight: 500, color: '#111', marginBottom: '4px' }}>Failed Test → Query</div><div style={{ fontSize: '20px', fontWeight: 600, color: '#111' }}>{sessionStats.full.failedTestMetrics.failedTestToQueryLatency !== null ? formatDuration(msToSec(sessionStats.full.failedTestMetrics.failedTestToQueryLatency)) : 'N/A'}</div><div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>Failed test to AI query</div></div>
                    </div>

                    {Object.keys(intentCounts).length > 0 && (
                      <div style={sectionStyle}>
                        <div style={sectionTitle}>Intent Classification</div>
                        {Object.entries(intentCounts).sort((a, b) => b[1] - a[1]).map(([intent, count]) => {
                          const info = intentLabels[intent] || { label: intent }
                          const pct = Math.round((count / userMessages.length) * 100)
                          return (
                            <div key={intent} style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e8e8eb', marginBottom: '6px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}><span style={{ fontSize: '12px', fontWeight: 500, color: '#111' }}>{info.label}</span><span style={{ fontSize: '12px', color: '#888' }}>{count} ({pct}%)</span></div>
                              <div style={{ height: '4px', backgroundColor: '#f0f0f2', borderRadius: '2px', overflow: 'hidden' }}><div style={{ height: '100%', width: `${pct}%`, backgroundColor: '#818cf8', borderRadius: '2px' }} /></div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </>
                )}

                {aiUsageView === 'code-impact' && (
                  <>
                    <div style={{ ...sectionStyle, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                      <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e8e8eb' }}><div style={{ fontSize: '12px', fontWeight: 500, color: '#111', marginBottom: '4px' }}>Response Reading Time</div><div style={{ fontSize: '20px', fontWeight: 600, color: '#111' }}>{sessionStats.full.chatMetrics.responseReadingTime !== null ? formatDuration(msToSec(sessionStats.full.chatMetrics.responseReadingTime)) : 'N/A'}</div><div style={{ fontSize: '11px', color: '#888', marginTop: '4px', lineHeight: 1.4 }}>Average time from AI response to the student's next action of any kind.</div></div>
                      <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e8e8eb' }}><div style={{ fontSize: '12px', fontWeight: 500, color: '#111', marginBottom: '4px' }}>Chat → Code Latency</div><div style={{ fontSize: '20px', fontWeight: 600, color: '#111' }}>{sessionStats.full.chatMetrics.chatToCodeLatency !== null ? formatDuration(msToSec(sessionStats.full.chatMetrics.chatToCodeLatency)) : 'N/A'}</div><div style={{ fontSize: '11px', color: '#888', marginTop: '4px', lineHeight: 1.4 }}>Average time from receiving an AI response to the next code edit.</div></div>
                      <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e8e8eb' }}><div style={{ fontSize: '12px', fontWeight: 500, color: '#111', marginBottom: '4px' }}>Copy from Chat</div><div style={{ fontSize: '20px', fontWeight: 600, color: '#111' }}>{sessionStats.full.frequency.chatHistoryCopy.count}</div><div style={{ fontSize: '11px', color: '#888', marginTop: '4px', lineHeight: 1.4 }}>Times the student selected and copied text from AI responses.</div></div>
                    </div>

                    {firstQueryTime !== null && (
                      <div style={sectionStyle}>
                        <div style={sectionTitle}>Before vs. After First AI Query</div>
                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '12px', lineHeight: 1.5 }}>Compares the student's coding behavior before and after their first AI interaction.</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e8e8eb' }}>
                            <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>Before Query ({formatDuration(Math.round(firstQueryTime))})</div>
                            {[['Code Edits', beforeStats.frequency.codeEdit.count], ['Edit Rate', `${beforeStats.frequency.codeEdit.perMin}/min`], ['Net Code Growth', `${beforeStats.codeMetrics.netCodeGrowth > 0 ? '+' : ''}${beforeStats.codeMetrics.netCodeGrowth}`], ['Pastes', beforeStats.frequency.codePaste.count], ['Runs', beforeStats.frequency.terminalRun.count], ['Errors', beforeStats.frequency.terminalError.count]].map(([label, val]) => (
                              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}><span style={{ fontSize: '12px', color: '#888' }}>{label}</span><span style={{ fontSize: '12px', fontWeight: 500, color: '#111' }}>{val}</span></div>
                            ))}
                          </div>
                          <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e8e8eb' }}>
                            <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>After Query ({formatDuration(msToSec(afterStats.periodDuration))})</div>
                            {[['Code Edits', afterStats.frequency.codeEdit.count], ['Edit Rate', `${afterStats.frequency.codeEdit.perMin}/min`], ['Net Code Growth', `${afterStats.codeMetrics.netCodeGrowth > 0 ? '+' : ''}${afterStats.codeMetrics.netCodeGrowth}`], ['Pastes', afterStats.frequency.codePaste.count], ['Runs', afterStats.frequency.terminalRun.count], ['Errors', afterStats.frequency.terminalError.count]].map(([label, val]) => (
                              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}><span style={{ fontSize: '12px', color: '#888' }}>{label}</span><span style={{ fontSize: '12px', fontWeight: 500, color: '#111' }}>{val}</span></div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    <div style={sectionStyle}>
                      <div style={sectionTitle}>Delete:Type Ratio Comparison</div>
                      <div style={{ fontSize: '11px', color: '#888', marginBottom: '12px', lineHeight: 1.5 }}>The proportion of keystrokes that were deletions vs. typing.</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div style={{ padding: '12px', borderRadius: '8px', border: '1px solid #e8e8eb' }}><div style={{ fontSize: '11px', color: '#888' }}>Before Query</div><div style={{ fontSize: '16px', fontWeight: 600, color: '#111', marginTop: '4px' }}>{beforeStats.codeMetrics.deletionToTypeRatio !== null ? `${(parseFloat(beforeStats.codeMetrics.deletionToTypeRatio) * 100).toFixed(0)}%` : 'N/A'}</div></div>
                        <div style={{ padding: '12px', borderRadius: '8px', border: '1px solid #e8e8eb' }}><div style={{ fontSize: '11px', color: '#888' }}>After Query</div><div style={{ fontSize: '16px', fontWeight: 600, color: '#111', marginTop: '4px' }}>{afterStats.codeMetrics.deletionToTypeRatio !== null ? `${(parseFloat(afterStats.codeMetrics.deletionToTypeRatio) * 100).toFixed(0)}%` : 'N/A'}</div></div>
                      </div>
                    </div>

                    {aiProgressionTimeline.length > 0 && (
                      <div style={sectionStyle}>
                        <div style={sectionTitle}>AI Progression Timeline</div>
                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '12px', lineHeight: 1.5 }}>A chronological view of every AI query and what happened after it.</div>
                        <div style={{ position: 'relative', paddingLeft: '20px' }}>
                          <div style={{ position: 'absolute', left: '7px', top: '4px', bottom: '4px', width: '2px', backgroundColor: '#e8e8eb' }} />
                          {aiProgressionTimeline.map((node, i) => {
                            if (node.type === 'query') {
                              return (
                                <div key={i} style={{ position: 'relative', marginBottom: '4px', paddingTop: '8px', paddingBottom: '8px' }}>
                                  <div style={{ position: 'absolute', left: '-17px', top: '12px', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#0070CC', border: '2px solid white', boxShadow: '0 0 0 1px #0070CC' }} />
                                  <div style={{ padding: '8px 12px', borderRadius: '6px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#0070CC', marginBottom: '2px' }}>{node.label}</div>
                                    {node.text && <div style={{ fontSize: '11px', color: '#555', lineHeight: 1.4, fontStyle: 'italic' }}>"{node.text}"</div>}
                                  </div>
                                </div>
                              )
                            }
                            const hasTests = node.testPassed !== null && node.testTotal !== null
                            const allPassed = hasTests && node.testPassed === node.testTotal
                            const growth = node.netCodeGrowth
                            const growthColor = growth > 0 ? '#10b981' : growth < 0 ? '#ef4444' : '#888'
                            return (
                              <div key={i} style={{ position: 'relative', marginBottom: '4px', paddingTop: '6px', paddingBottom: '6px' }}>
                                <div style={{ position: 'absolute', left: '-15px', top: '10px', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#9ca3af' }} />
                                <div style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e8e8eb' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                                    <div style={{ fontSize: '11px', color: '#555', minWidth: 0 }}>{node.label}{node.duration > 0 && <span style={{ color: '#aaa', marginLeft: '6px' }}>({formatDuration(Math.round(node.duration / 1000))})</span>}</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                      {hasTests && <span style={{ fontSize: '11px', fontWeight: 600, color: allPassed ? '#10b981' : '#f59e0b', padding: '2px 6px', borderRadius: '4px', backgroundColor: allPassed ? '#ecfdf5' : '#fffbeb' }}>{node.testPassed}/{node.testTotal}</span>}
                                      <span style={{ fontSize: '11px', fontWeight: 600, color: growthColor }}>{growth > 0 ? '+' : ''}{growth} chars</span>
                                    </div>
                                  </div>
                                  {(node.testDelta !== 0 || node.errorDelta !== 0) && (
                                    <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                                      {node.testDelta !== 0 && <span style={{ fontSize: '10px', color: node.testDelta > 0 ? '#10b981' : '#ef4444' }}>Tests {node.testDelta > 0 ? '+' : ''}{node.testDelta}</span>}
                                      {node.errorDelta !== 0 && <span style={{ fontSize: '10px', color: node.errorDelta > 0 ? '#ef4444' : '#10b981' }}>Errors {node.errorDelta > 0 ? '+' : ''}{node.errorDelta}</span>}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {aiUsageView === 'transcript' && (
                  <div style={sectionStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <span style={sectionTitle}>Chat Transcript</span>
                      <span style={{ fontSize: '11px', color: '#888' }}>{messageCount} message{messageCount !== 1 ? 's' : ''}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {conversation.map((msg, i) => (
                        <div key={i} style={{ display: 'flex', gap: '8px', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
                          <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#f0f0f2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>{msg.role === 'user' ? 'U' : 'AI'}</div>
                          <div style={{ maxWidth: '80%' }}>
                            <div style={{ padding: '10px 14px', borderRadius: '10px', fontSize: '13px', lineHeight: 1.5, wordBreak: 'break-word', backgroundColor: msg.role === 'user' ? '#0070CC' : '#f5f5f7', color: msg.role === 'user' ? 'white' : '#333', border: msg.role === 'user' ? 'none' : '1px solid #e8e8eb' }}>{msg.content}</div>
                            {msg.role === 'user' && msg.intent && <div style={{ fontSize: '10px', color: '#888', marginTop: '4px', textAlign: 'right' }}>{intentLabels[msg.intent]?.label || msg.intent}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Behavior ──────────────────────────────────────────────── */}
        {activeTab === 'behavior' && (
          <div>
            <div style={{ ...sectionStyle, display: 'flex', gap: '8px' }}>
              {[{ value: 'full', label: 'Full Session' }, { value: 'before-query', label: 'Before First Query' }, { value: 'after-query', label: 'After First Query' }].map(opt => {
                const disabled = opt.value !== 'full' && firstQueryTime === null
                return (<button key={opt.value} disabled={disabled} onClick={() => setBehaviorScope(opt.value)} style={{ padding: '6px 14px', fontSize: '12px', fontWeight: 500, borderRadius: '6px', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', backgroundColor: behaviorScope === opt.value ? '#0070CC' : disabled ? '#f0f0f2' : '#f5f5f5', color: behaviorScope === opt.value ? 'white' : disabled ? '#ccc' : '#555' }}>{opt.label}</button>)
              })}
            </div>

            {behaviorScope !== 'full' && firstQueryTime !== null && (
              <div style={{ ...sectionStyle, padding: '10px 14px', borderRadius: '8px', border: '1px solid #e8e8eb', backgroundColor: '#fafafa' }}>
                <span style={{ fontSize: '12px', color: '#888' }}>{behaviorScope === 'before-query' ? `Showing first ${formatDuration(Math.round(firstQueryTime))} before first AI query. Duration: ${formatDuration(scopedDuration)}.` : `Showing ${formatDuration(scopedDuration)} after first AI query.`}</span>
              </div>
            )}

            <div style={sectionStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={sectionTitle}>Time by Region</div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {['stats', 'chart'].map(v => (<button key={v} onClick={() => setRegionView(v)} style={{ padding: '3px 10px', fontSize: '11px', fontWeight: 500, borderRadius: '4px', border: 'none', cursor: 'pointer', backgroundColor: regionView === v ? '#0070CC' : '#f5f5f5', color: regionView === v ? 'white' : '#555' }}>{v === 'stats' ? 'Stats' : 'Chart'}</button>))}
                </div>
              </div>
              <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e8e8eb' }}>
                {(() => {
                  const regions = [{ key: 'codeEditor', label: 'Code Editor', color: '#3b82f6' }, { key: 'chat', label: 'Chat', color: '#8b5cf6' }, { key: 'terminal', label: 'Terminal', color: '#10b981' }, { key: 'taskDescription', label: 'Task', color: '#f59e0b' }, { key: 'testPanel', label: 'Tests', color: '#ef4444' }]
                  const total = Object.values(derivedBehavior.timeByRegion).reduce((a, b) => a + b, 0) || 1

                  if (regionView === 'chart') {
                    const size = 180, cx = size / 2, cy = size / 2, r = 70
                    let cumAngle = -Math.PI / 2
                    const slices = regions.filter(reg => derivedBehavior.timeByRegion[reg.key] > 0).map(reg => {
                      const val = derivedBehavior.timeByRegion[reg.key]
                      const angle = (val / total) * Math.PI * 2
                      const startAngle = cumAngle; cumAngle += angle; const endAngle = cumAngle
                      const largeArc = angle > Math.PI ? 1 : 0
                      const x1 = cx + r * Math.cos(startAngle), y1 = cy + r * Math.sin(startAngle)
                      const x2 = cx + r * Math.cos(endAngle), y2 = cy + r * Math.sin(endAngle)
                      return { ...reg, val, path: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z` }
                    })
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>{slices.length > 0 ? slices.map((s, i) => (<path key={i} d={s.path} fill={s.color} stroke="white" strokeWidth="1.5" />)) : (<circle cx={cx} cy={cy} r={r} fill="#f0f0f2" />)}</svg>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px 16px' }}>
                          {regions.map(reg => { const val = derivedBehavior.timeByRegion[reg.key]; const pct = Math.round((val / total) * 100); return (<div key={reg.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: reg.color, flexShrink: 0 }} /><span style={{ fontSize: '11px', color: '#555' }}>{reg.label}</span><span style={{ fontSize: '11px', fontWeight: 500, color: '#111' }}>{formatDuration(val)}</span><span style={{ fontSize: '10px', color: '#aaa' }}>({pct}%)</span></div>) })}
                        </div>
                      </div>
                    )
                  }
                  return (<>{regions.map(r => (<div key={r.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}><div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: r.color }} /><span style={{ fontSize: '11px', color: '#555' }}>{r.label}</span></div><span style={{ fontSize: '11px', fontWeight: 500, color: '#111' }}>{formatDuration(derivedBehavior.timeByRegion[r.key])}</span></div>))}</>)
                })()}
              </div>
            </div>

            <div style={sectionStyle}>
              <div style={sectionTitle}>Event Frequency</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                {[{ label: 'Edits', value: derivedBehavior.totalEdits }, { label: 'Runs', value: derivedBehavior.totalRuns }, { label: 'Errors', value: derivedBehavior.totalErrors }, { label: 'Copy/Paste', value: derivedBehavior.copyPasteCount }, { label: 'Tab Switches', value: derivedBehavior.tabSwitches }, { label: 'AI Queries', value: derivedBehavior.aiQueries }].map(item => (
                  <div key={item.label} style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #e8e8eb' }}><div style={{ fontSize: '11px', color: '#888' }}>{item.label}</div><div style={{ fontSize: '14px', fontWeight: 600, color: '#111' }}>{item.value}</div></div>
                ))}
              </div>
            </div>

            <div style={sectionStyle}>
              <div style={sectionTitle}>Idle Time</div>
              <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e8e8eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}><span style={{ fontSize: '12px', color: '#888' }}>Scope Duration</span><span style={{ fontSize: '12px', fontWeight: 500, color: '#111' }}>{formatDuration(scopedDuration)}</span></div>
                <div style={{ display: 'flex', height: '8px', borderRadius: '4px', overflow: 'hidden' }}><div style={{ width: `${scopedDuration > 0 ? (derivedBehavior.activeTime / scopedDuration) * 100 : 100}%`, backgroundColor: '#818cf8' }} /><div style={{ width: `${scopedDuration > 0 ? (derivedBehavior.idleTime / scopedDuration) * 100 : 0}%`, backgroundColor: '#d1d5db' }} /></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}><div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#818cf8' }} /><span style={{ fontSize: '11px', color: '#888' }}>Active ({formatDuration(derivedBehavior.activeTime)})</span></div><div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#d1d5db' }} /><span style={{ fontSize: '11px', color: '#888' }}>Idle ({formatDuration(derivedBehavior.idleTime)})</span></div></div>
                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e8e8eb', display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '12px', color: '#888' }}>Longest Idle Period</span><span style={{ fontSize: '12px', fontWeight: 500, color: '#111' }}>{formatDuration(derivedBehavior.longestIdle)}</span></div>
              </div>
            </div>

            <div style={sectionStyle}>
              <div style={sectionTitle}>Code Metrics</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <MetricRow label="Keystroke Rate" value={`${derivedBehavior.keystrokeRate}/min`} desc="Average typing speed in this scope" />
                <MetricRow label="Net Code Growth" value={`${derivedBehavior.netCodeGrowth > 0 ? '+' : ''}${derivedBehavior.netCodeGrowth}`} desc="Keystrokes minus deletes" />
                <MetricRow label="Total Inserted" value={`${derivedBehavior.totalInserted} chars`} desc="Total characters typed or pasted" />
                <MetricRow label="Total Deleted" value={`${derivedBehavior.totalDeleted} chars`} desc="Total characters removed" />
                <MetricRow label="Delete:Type Ratio" value={`${(derivedBehavior.deleteTypeRatio * 100).toFixed(0)}%`} desc="Proportion of keystrokes that were deletes" />
              </div>
            </div>

            <div style={sectionStyle}>
              <div style={sectionTitle}>Terminal Error Response ({derivedBehavior.errorCount} errors)</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <MetricRow label="Reading Time" value={derivedBehavior.errorReadingTime > 0 ? formatDuration(derivedBehavior.errorReadingTime) : 'N/A'} desc="Avg time from error to any next action" />
                <MetricRow label="→ Edit" value={derivedBehavior.errorToEditLatency > 0 ? formatDuration(derivedBehavior.errorToEditLatency) : 'N/A'} desc="Avg time from error to next code edit" />
                <MetricRow label="→ AI Query" value={derivedBehavior.errorToQueryLatency > 0 ? formatDuration(derivedBehavior.errorToQueryLatency) : 'N/A'} desc="Avg time from error to asking AI for help" />
                <MetricRow label="→ Resolution" value={derivedBehavior.errorToResolution > 0 ? formatDuration(derivedBehavior.errorToResolution) : 'N/A'} desc="Avg time from error to next test score improvement" />
                <MetricRow label="First Response" value={derivedBehavior.errorCount > 0 ? `${derivedBehavior.errorSelfFixCount} self-fix / ${derivedBehavior.errorAiFixCount} AI` : 'N/A'} desc="After each error, did the student edit code first (self-fix) or query AI first?" />
              </div>
            </div>

            <div style={sectionStyle}>
              <div style={sectionTitle}>Failed Test Response ({derivedBehavior.failedTestCount} failed runs)</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <MetricRow label="Reading Time" value={derivedBehavior.failedTestReadingTime > 0 ? formatDuration(derivedBehavior.failedTestReadingTime) : 'N/A'} desc="Avg time from failed test to any next action" />
                <MetricRow label="→ Edit" value={derivedBehavior.failedTestToEditLatency > 0 ? formatDuration(derivedBehavior.failedTestToEditLatency) : 'N/A'} desc="Avg time from failed test to next code edit" />
                <MetricRow label="→ AI Query" value={derivedBehavior.failedTestToQueryLatency > 0 ? formatDuration(derivedBehavior.failedTestToQueryLatency) : 'N/A'} desc="Avg time from failed test to asking AI for help" />
                <MetricRow label="→ Resolution" value={derivedBehavior.failedTestToResolution > 0 ? formatDuration(derivedBehavior.failedTestToResolution) : 'N/A'} desc="Avg time from failed test to next test score improvement" />
                <MetricRow label="First Response" value={derivedBehavior.failedTestCount > 0 ? `${derivedBehavior.failedTestSelfFixCount} self-fix / ${derivedBehavior.failedTestAiFixCount} AI` : 'N/A'} desc="After each failed test, did the student edit code first (self-fix) or query AI first?" />
              </div>
            </div>

            <div style={sectionStyle}>
              <div style={sectionTitle}>Terminal Run Cadence</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <MetricRow label="Mean Time Between Runs" value={derivedBehavior.meanTimeBetweenRuns > 0 ? formatDuration(derivedBehavior.meanTimeBetweenRuns) : 'N/A'} desc="Average gap between code executions" />
                <MetricRow label="Max Consecutive Errors" value={`${derivedBehavior.consecutiveErrors}`} desc="Errors in a row without meaningful progress" />
              </div>
            </div>

            <div style={sectionStyle}>
              <div style={sectionTitle}>Distraction Data</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ padding: '12px', borderRadius: '8px', border: '1px solid #e8e8eb' }}><div style={{ fontSize: '11px', color: '#888' }}>Times Switched Away</div><div style={{ fontSize: '14px', fontWeight: 600, color: '#111', marginTop: '2px' }}>{derivedBehavior.tabSwitches}</div></div>
                <div style={{ padding: '12px', borderRadius: '8px', border: '1px solid #e8e8eb' }}><div style={{ fontSize: '11px', color: '#888' }}>Time Away</div><div style={{ fontSize: '14px', fontWeight: 600, color: scopedStats.tabVisibility.hiddenTime > 10000 ? '#ef4444' : '#111', marginTop: '2px' }}>{formatDuration(msToSec(scopedStats.tabVisibility.hiddenTime))}</div></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default StudentDetail